import { CreateOrganizationProductRequest, GetOrganizationProductResponse, OrganizationProductStatus, PaymentTargetType, PurchaseSummaryResponse } from '../types';
import { LitElement, css, html } from 'lit';
import { archiveOrganizationProduct, formatPrice, getPriceStringFromProduct, getPriceTypeFromProduct } from '../helpers/api/products-api';
import { customElement, property, state } from 'lit/decorators';

import { getMainPage } from '../helpers/page-name';
import { isDefined } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';

@customElement('existing-products-component')
export class ExistingProductsComponent extends LitElement {

    @property({ type: Array })
    data: GetOrganizationProductResponse[] = [];

    @property()
    private readonly payment_target_type: PaymentTargetType = PaymentTargetType.Form;

    @state()
    purchases: PurchaseSummaryResponse[] = [];

    @state()
    sortBy: string = '';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    @state()
    products: GetOrganizationProductResponse[] = [];

    @state()
    previewMode: boolean = false;

    @state()
    previewProduct?: GetOrganizationProductResponse | CreateOrganizationProductRequest;

    @state()
    totalPerProduct: Record<string, number> = {};

    @state()
    selectedTab?: number = undefined;

    amplitude_callsite: string = 'Existing Products Modal';

    options = ['Active', 'Archived'];

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data' || propName === 'purchases') {
                // Sort using string comparison
                this.products = this.data.sort((a, b) => {
                    return b.created_at.toString().localeCompare(a.created_at.toString());
                });

                this.totalPerProduct = {};
                for (const purchase of this.purchases) {
                    for (const item of purchase.purchase_items) {
                        const productId = item.product_id.$oid;
                        if (!this.totalPerProduct[productId]) {
                            this.totalPerProduct[productId] = 0;
                        }
                        this.totalPerProduct[productId] += item.price_cents * item.quantity;
                    }
                }
            }
        });
    }

    private changeTab(e: CustomEvent | any) {
        this.selectedTab = e.detail.tabIndex;
        const value = this.options[this.selectedTab ?? 0];

        this.changeMessageType(value === 'Active' ? OrganizationProductStatus.Active
            : OrganizationProductStatus.Archived);
    }

    private changeMessageType(status: OrganizationProductStatus) {
        if (status === OrganizationProductStatus.Active) {
            this.products = this.data.filter((product) => product.status === OrganizationProductStatus.Active);
        } else if (status === OrganizationProductStatus.Archived) {
            this.products = this.data.filter((product) => product.status === OrganizationProductStatus.Archived);
        } else {
            this.products = this.data;
        }
    }

    private sortClickHandler(sortBy: string) {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }

        this.products = this.products.sort((a, b) => {
            const comparison = 0;
            if (sortBy === 'name') {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                return nameA.localeCompare(nameB);
            } else if (sortBy === 'status') {
                return a.status.localeCompare(b.status);
            } else if (sortBy === 'pricetype') {
                const priceTypeA = getPriceTypeFromProduct(a);
                const priceTypeB = getPriceTypeFromProduct(b);
                return priceTypeA.localeCompare(priceTypeB);
            }

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    private async openPreview(product: GetOrganizationProductResponse | CreateOrganizationProductRequest) {
        this.previewMode = true;
        this.previewProduct = product;
        await this.updateComplete;
    }

    private async setEditableProduct(product: GetOrganizationProductResponse) {
        this.dispatchEvent(new CustomEvent('set-edited-product', {
            bubbles: true,
            composed: true,
            detail: { product }
        }));
    }

    private async stopProduct(product: GetOrganizationProductResponse) {
        if (!confirm(`Are you sure you want to archive this product?`)) {
            // eslint-disable-next-line no-useless-return
            return;
        }
        await archiveOrganizationProduct(topicData.topic?.organization_id?.$oid ?? '', product._id.$oid, topicData.topicKey);

        getMainPage().errorToastElement?.showToast('Product archived successfully');

        this.dispatchEvent(new CustomEvent('update-products'));
    }


    render() {
        if (this.previewMode  && isDefined(this.previewProduct)) {
            return html`
        <div class="preview-container">
                <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center;">
                    <div class="preview-subject">
                        <h3>Preview</h3>
                    </div>
                    <form-button
                        variant="outline"
                        size="sm"
                        class="close-button"
                        @click="${() => { this.previewMode = false; this.previewProduct = undefined; }}"
                        >
                        Close Preview
                    </form-button>
                </div>
                <div class="sms-preview-content" style="background: #f5f5f5; padding: 16px; border-radius: 8px; margin-top: 16px; font-family: monospace; border: 1px solid #ddd;">
                    <pre-checkout-component
                        .product_ids=${[this.previewProduct?._id?.$oid]}
                        .isPreview=${true}
                        .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                    >
                    </pre-checkout-component>
                </div>
                <br />
                Users will be asked to pay upon completion of the form.
            </div>
            `;
        }

        return html`
                <tabs-component id="tabsComponent"
                    .options=${this.options}
                    .type=${'pill'}
                    style="width: 100%;margin-bottom: 10px; margin-top: 10px;"
                    .tabIndex=${this.selectedTab}
                    @on-select-tab=${(e: CustomEvent) => {
                        this.changeTab(e);
                    }}
                ></tabs-component>
                <div class="challenges-wrapper">
                    <div class="header-row">
                        <div class="header-cell text" @click="${() => this.sortClickHandler('name')}">Name ${this.sortBy === 'name' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number" @click="${() => this.sortClickHandler('status')}">Status ${this.sortBy === 'status' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number" @click="${() => this.sortClickHandler('pricetype')}">Price ${this.sortBy === 'pricetype' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number">Total Revenue</div>
                        <div class="header-cell number">Actions</div>
                    </div>
                    ${this.products.map((product) => html`
                        <div class="challenge-row">
                            <div class="data-cell text">
                                ${product.name}
                            </div>
                            <div class="data-cell number">
                                ${product.status}
                            </div>
                            <div class="data-cell number">${getPriceStringFromProduct(product)}</div>
                            <div class="data-cell number">
                                ${formatPrice(this.totalPerProduct[product._id.$oid] ?? 0, product.currency_code)}
                            </div>
                            <div class="data-cell number" style="display: flex;flex-wrap: wrap;justify-content: flex-start;gap: 2px;">
                                ${product.status === OrganizationProductStatus.Archived ? html`<div>N/A</div>` : html`
                                    ${this.payment_target_type === PaymentTargetType.Paywall ? html`
                                        <form-button variant="outline" size="xs" .icon=${'link'} class="aqua-blue" @click="${() => this.getGateUrl(product)}">Get Gate Link</form-button>
                                    ` : ''}
                                    <form-button variant="outline" size="xs" .icon=${'edit'} class="aqua-blue" @click="${async () => await this.setEditableProduct(product)}">Edit</form-button>
                                    <form-button variant="outline" size="xs" .icon=${'visibility'} class="aqua-blue" @click="${async () => await this.openPreview(product)}">View</form-button>
                                    <form-button variant="outline" size="xs" .icon=${'pause'} class="aqua-blue" @click="${async () => await this.stopProduct(product)}">Archive</form-button>
                                ` }
                            </div>
                        </div>
                    `)}
                </div>
            </div>
        `;
    }

    private getGateUrl(product: GetOrganizationProductResponse) {
        if (!('Paywall' in product.item_details)) {
            getMainPage().errorToastElement?.showToast('Unable to get gate link for this product.');
            return;
        }
        // send custom event to parent
        this.dispatchEvent(new CustomEvent('copy-payment-link', { detail: { gate_id: product.item_details?.Paywall?.gate_id?.$oid } }));
    }

    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
                font-size: 12px;
                display: flex;
                flex-direction: column;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }



            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 25%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.channel {
                text-align: center;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 20%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 25%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 20%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .challenges-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
                cursor: context-menu;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}